import { Link } from 'react-router-dom';
import { ArrowRightIcon, ChevronDownIcon } from 'lucide-react';
import { ShopFaq } from '../shared/ShopFaq';
import { SubscriptionBlock } from '../shared/SubscriptionBlock';
import { pageActions } from '../../data/pageActions';
import { HeadingText } from '../shared/HeadingText';
import { PageHero } from '../shared/PageHero';
import { ConcernFinder } from '../home/ConcernFinder';
import { HomeProtocols } from '../home/HomeProtocols';
import { useCatalog } from '../../contexts/CatalogContext';
import { useOverlayActions } from '../../contexts/StoreContext';
import { founder } from '../../data/content';
import { usePages } from '../../contexts/SiteContext';
import { fill } from '../../utils/copy';
import { mediaOr } from '../../core/rules/media';
import { btnPrimaryLg, btnSecondaryLg } from '../../ui/styles';
import { quietLink } from '../../ui/sections';
import { ClosingBand } from '../shared/ClosingBand';
import { plural } from '../../core/format/text';
import { RoutineCompare } from './RoutineCompare';
import { RoutineAnchor } from './RoutineAnchor';
import { anchorRoutine, ROUTINES_FAQ_COUNT } from './routineRules';

/**
 * Protocollen: de pagina met de hoogste orderwaarde. Volgorde = de vragen van een bezoeker
 * (docs/VORMGEVING.md §3): wat is een protocol (hero, sand) · het complete protocol, met wat je
 * er cadeau bij krijgt (blush) · of begin bij je klacht: de klachtkiezer van Home, rustig, met
 * vergelijken in een uitklapper (shell) · wat er na je bestelling gebeurt: de tijdlijn van Home,
 * stil (sand) · abonnement (kort) · vragen · twijfel: de huidtest, met de code voor salonklanten
 * (blush; de footer is klei). Kopen is hier de hoofdactie: boeken staat in de kiezer als link.
 *
 * Klachtkiezer en tijdlijn zijn dezelfde onderdelen als op Home (één bron), hier zonder beweging:
 * Home is de speelse ontvangst, dit is een winkelpagina.
 */
export function Routines() {
  const pages = usePages();
  const {
    openQuiz
  } = useOverlayActions();
  const {
    routines,
    routineTotals
  } = useCatalog();
  const copy = pages.routines;
  const complete = anchorRoutine(routines, r => routineTotals(r).price);
  const heroMedia = mediaOr(copy.hero.media, {
    image: complete?.image || founder.withProducts,
    alt: complete ? `De producten van ${complete.name} op een rij` : `${founder.name} met de producten waar ze mee werkt`
  });
  return <>
      {/* Hero: zelfde opbouw als salon en over. In het beeld: wie het opstelt, en wat je meekrijgt. */}
      <PageHero titleId="routines-title" eyebrow={copy.hero.eyebrow ? fill(copy.hero.eyebrow) : undefined} title={<HeadingText heading={copy.hero.heading} accentBlock data-id="element-8615" data-mp="2eoctczkvzvn6" />} body={copy.hero.body ? fill(copy.hero.body) : undefined} media={heroMedia} byline={{
      lead: <>
              Persoonlijk huidplan, opgesteld door <span className="text-ink" data-id="element-8616" data-mp="2ojo9nmby1ax1">{founder.firstName}</span>
            </>,
      // "Vegan" alleen op de Webshop-opening en in de footer (docs/VORMGEVING.md › Bewust dubbel).
      sub: plural(routines.length, 'protocol', 'protocollen')
    }} actions={/* Hoofdknop: naar de protocollen zelf. Wie nog twijfelt, doet eerst de huidtest. */
    <>
            <a href={complete ? '#anchor-routine' : '#concern'} className={btnPrimaryLg} data-id="element-8617" data-mp="80vwwmq8drgb">
              {pageActions.startProtocol}
            </a>
            {copy.hero.cta && <button type="button" onClick={openQuiz} aria-haspopup="dialog" className={btnSecondaryLg} data-id="element-8618" data-mp="10pt4nvwtg5ut">
                {fill(copy.hero.cta)}
              </button>}
          </>} data-id="element-8614" data-mp="29wcwxh40zkvc" />

      {complete && <RoutineAnchor routine={complete} data-id="element-8619" data-mp="lytzyvz3q1gi" />}

      {/* Of begin bij je klacht: de klachtkiezer van Home, rustig. De huidtest staat al in de opening. */}
      <ConcernFinder copy={{
      ...copy.concerns,
      quizLine: ''
    }} still data-id="element-8620" data-mp="1jezq3qozvbkb">
        {routines.length > 1 && <details className="group mt-14 border-t border-ink/15 pt-6" data-id="element-8621" data-mp="1elya1fd1x5z5">
            <summary className="flex min-h-[44px] cursor-pointer list-none items-center justify-between gap-4 text-ink [&::-webkit-details-marker]:hidden" data-id="element-8622" data-mp="z589u3yung3c">
              <span className="type-title" data-id="element-8623" data-mp="14288httddgkf">{fill(copy.compareHeading)}</span>
              <ChevronDownIcon className="h-5 w-5 shrink-0 transition-transform duration-200 ease-soft group-open:rotate-180" strokeWidth={1.5} aria-hidden="true" data-id="element-8624" data-mp="p94zwbxrad5u" />
            </summary>
            <RoutineCompare heading={fill(copy.compareHeading)} hideHeading data-id="element-8625" data-mp="1hwhmun7twmzo" />
          </details>}

        {copy.smallStart.body && <div className="mt-10 flex max-w-3xl flex-wrap items-baseline gap-x-6 gap-y-3" data-id="element-8626" data-mp="186659xv82fab">
            <p className="text-[17px] leading-relaxed text-muted" data-id="element-8627" data-mp="184btpa5ubziw">{fill(copy.smallStart.body)}</p>
            {copy.smallStart.shopCta && <Link to="/webshop" className={`${quietLink} text-ink`} data-id="element-8628" data-mp="20tslc5lmzce2">
                {fill(copy.smallStart.shopCta)}
                <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-8629" data-mp="25n4wbcxe7koc" />
              </Link>}
          </div>}
      </ConcernFinder>

      {/* Wat er na je bestelling gebeurt: de tijdlijn van Home, stil. Eigen kop, dezelfde momenten. */}
      <HomeProtocols copy={{
      ...copy.buildUp,
      eyebrow: '',
      cta: ''
    }} still titleId="routines-build-up" data-id="element-8630" data-mp="3wnywr165iz3" />

      {/* Abonnement en vragen: dezelfde blokken als op de webshop, hier in de korte versie. */}
      <SubscriptionBlock size="short" data-id="element-8631" data-mp="37e65kv57gjxx" />

      <ShopFaq limit={ROUTINES_FAQ_COUNT} data-id="element-8632" data-mp="37f7i741vdehr" />

      {/* Het slot van de funnel: wie nog twijfelt doet de huidtest (WhatsApp staat al onder de koopknop);
          salonklanten lezen hier hun code. Op blush: de footer is al klei. */}
      <ClosingBand tone="blush" titleId="salon-client" title={<HeadingText heading={copy.salonClient.heading} data-id="element-8634" data-mp="3cekdmd6t3tvy" />} body={copy.salonClient.body ? fill(withoutCodeSentence(copy.salonClient.body)) || undefined : undefined} actions={copy.salonClient.cta && <button type="button" onClick={openQuiz} aria-haspopup="dialog" className={btnPrimaryLg} data-id="element-8635" data-mp="3kgz14ahwbkfi">
              {fill(copy.salonClient.cta)}
            </button>} data-id="element-8633" data-mp="3ccfzl2f71ehy" />
    </>;
}

/** Eigen tekst van Sarah met nog een `{code}`-zin: die zin valt weg, de saloncode staat niet meer op de pagina. */
const withoutCodeSentence = (text: string) => text.split(/(?<=[.!?])\s+/).filter(s => !s.includes('{code}')).join(' ');